/**
 * Dalla serie giornaliera del rendimento (F-182) a ciò che il grafico disegna:
 * candele giornaliere o settimanali, buchi, riepiloghi.
 *
 * Funzioni pure, separate dal wrapper di `lightweight-charts`: è qui che stanno
 * le scelte da verificare, e in jsdom il canvas non si può guardare.
 *
 * ⚠️ I valori diventano `number` **solo** per disegnare e per le differenze
 * mostrate a schermo (ADR-0006, come `sumOf` del dettaglio): nessun importo
 * calcolato qui torna al server o finisce in un totale da riconciliare.
 */

import type { PerformanceDay, PerformanceOperation, PortfolioPerformance } from './api';

export type PerformanceMetric = 'unrealized' | 'realized' | 'combined';
export type CandleWidth = 'day' | 'week';

export const METRICS: readonly PerformanceMetric[] = ['unrealized', 'realized', 'combined'];

export const METRIC_LABELS: Record<PerformanceMetric, string> = {
  unrealized: 'Non realizzata',
  realized: 'Realizzata',
  combined: 'Combinata',
};

export function isMetric(value: string | null): value is PerformanceMetric {
  return value === 'unrealized' || value === 'realized' || value === 'combined';
}

function toNumber(value: string | null): number | null {
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function valueOf(day: PerformanceDay, metric: PerformanceMetric): number | null {
  return toNumber(day[metric]);
}

/**
 * Un secchio del grafico: un giorno, o una settimana per le candele
 * settimanali. `time` è il primo giorno del secchio, ed è la chiave con cui il
 * grafico lo restituisce sotto il cursore.
 */
export type PerformanceBucket = {
  time: string;
  from: string;
  to: string;
  days: PerformanceDay[];
  operations: PerformanceOperation[];
  /** Chiusura del secchio precedente con un valore: dove la candela parte. */
  open: number | null;
  /** `null` = nessun valore nel secchio (giorni senza snapshot). */
  close: number | null;
  high: number | null;
  low: number | null;
};

/** Lunedì della settimana di `date`, nel calendario UTC come la serie. */
export function weekStart(date: string): string {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() - ((value.getUTCDay() + 6) % 7));
  return value.toISOString().slice(0, 10);
}

/**
 * Raggruppa i giorni e calcola apertura, massimo, minimo e chiusura.
 *
 * La candela giornaliera apre alla chiusura dell'ultimo giorno **con un
 * valore** — non a zero, e non al giorno di calendario precedente se quello
 * non ha fotografia. Con un solo valore al giorno non ha ombre: il corpo è
 * tutto ciò che si sa. Quella settimanale le ha davvero: il giorno migliore e
 * il peggiore della settimana.
 */
export function buildBuckets(
  performance: Pick<PortfolioPerformance, 'days' | 'opening'>,
  metric: PerformanceMetric,
  width: CandleWidth,
): PerformanceBucket[] {
  const groups: PerformanceDay[][] = [];
  let currentKey: string | null = null;
  for (const day of performance.days) {
    const key = width === 'week' ? weekStart(day.date) : day.date;
    if (key !== currentKey) {
      groups.push([]);
      currentKey = key;
    }
    groups[groups.length - 1]!.push(day);
  }

  let previous = toNumber(performance.opening[metric]);
  return groups.map((days) => {
    const values = days.map((day) => valueOf(day, metric)).filter((value): value is number => value !== null);
    const close = values.at(-1) ?? null;
    const open = close === null ? previous : (previous ?? values[0]!);
    const bucket: PerformanceBucket = {
      time: width === 'week' ? weekStart(days[0]!.date) : days[0]!.date,
      from: days[0]!.date,
      to: days.at(-1)!.date,
      days,
      operations: days.flatMap((day) => day.operations),
      open,
      close,
      high: close === null ? null : Math.max(open!, ...values),
      low: close === null ? null : Math.min(open!, ...values),
    };
    if (close !== null) previous = close;
    return bucket;
  });
}

/** Tratto senza valori fra due secchi che ne hanno: si disegna tratteggiato, mai riempito. */
export type PerformanceGap = { from: { time: string; value: number }; to: { time: string; value: number } };

export function findGaps(buckets: readonly PerformanceBucket[]): PerformanceGap[] {
  const gaps: PerformanceGap[] = [];
  let last: { time: string; value: number; index: number } | null = null;
  buckets.forEach((bucket, index) => {
    if (bucket.close === null) return;
    if (last && index - last.index > 1) {
      gaps.push({ from: { time: last.time, value: last.value }, to: { time: bucket.time, value: bucket.close } });
    }
    last = { time: bucket.time, value: bucket.close, index };
  });
  return gaps;
}

export type MetricSummary = {
  /** L'ultimo giorno con un valore: per la non realizzata può non essere oggi. */
  latest: { date: string; value: string } | null;
  /** Variazione dello stato nel periodo, ai soli fini di visualizzazione. */
  change: number | null;
};

/**
 * Lo stato più recente di una curva e quanto è cambiato nel periodo, contro
 * l'apertura (l'ultimo valore prima della finestra) o, in mancanza, il primo
 * valore della finestra.
 */
export function summarize(
  performance: Pick<PortfolioPerformance, 'days' | 'opening'>,
  metric: PerformanceMetric,
): MetricSummary {
  const withValue = performance.days.filter((day) => day[metric] !== null);
  const latestDay = withValue.at(-1);
  if (!latestDay) return { latest: null, change: null };
  const baseline = toNumber(performance.opening[metric]) ?? valueOf(withValue[0]!, metric);
  const latest = valueOf(latestDay, metric);
  return {
    latest: { date: latestDay.date, value: latestDay[metric]! },
    change: baseline === null || latest === null ? null : latest - baseline,
  };
}

export type OperationKind = 'buy' | 'sell' | 'income' | 'other';

/** Il verso dell'operazione, per il segno sul grafico. */
export function operationKind(txType: string): OperationKind {
  if (txType === 'buy' || txType === 'savings_plan') return 'buy';
  if (txType === 'sell') return 'sell';
  if (txType === 'dividend' || txType === 'staking_reward' || txType === 'interest') return 'income';
  return 'other';
}

/** Il secchio da mostrare aperto all'arrivo: l'ultimo con una vendita, se c'è, altrimenti l'ultimo. */
export function defaultSelection(buckets: readonly PerformanceBucket[]): string | null {
  for (let index = buckets.length - 1; index >= 0; index -= 1) {
    if (buckets[index]!.operations.some((operation) => operation.sale !== null)) return buckets[index]!.time;
  }
  return buckets.at(-1)?.time ?? null;
}
